/**
 * What this phone predicted for an event, kept in localStorage so the fan page can
 * show the score again after a reload, follow it live and settle it at full time.
 *
 * Key: predictor_prediction_<eventId>. The older boolean flag predictor_completed_<eventId>
 * stays as it is; this file only adds the details next to it.
 */

export type RememberedPrediction = {
  id: string;
  home: number;
  away: number;
  prize: string | null;
  /** ISO timestamp of the submission */
  at: string;
};

export type PredictionOutcome = 'pending' | 'live' | 'correct' | 'wrong';

export type LiveMatchState = {
  status: string | null | undefined;
  home: number | null | undefined;
  away: number | null | undefined;
};

/** Statuses where the match has not (yet) produced a result to compare against. */
const PENDING_STATUSES = new Set(['not_started', 'postponed', 'cancelled', 'abandoned', 'suspended', 'delayed']);
/** Statuses where the score on record is the final one. */
const FINAL_STATUSES = new Set(['finished', 'full_time', 'ended', 'after_extra_time', 'after_penalties']);

export const predictionStorageKey = (eventId: string) => `predictor_prediction_${eventId}`;

const storage = (): Storage | null => {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
};

export function rememberPrediction(
  eventId: string,
  pred: { id: string; home: number; away: number; at: string; prize?: string | null },
): void {
  const value: RememberedPrediction = { id: pred.id, home: pred.home, away: pred.away, prize: pred.prize ?? null, at: pred.at };
  try {
    storage()?.setItem(predictionStorageKey(eventId), JSON.stringify(value));
  } catch {
    /* private mode / quota: the page simply forgets after a reload */
  }
}

export function readPrediction(eventId: string | null | undefined): RememberedPrediction | null {
  if (!eventId) return null;
  try {
    const raw = storage()?.getItem(predictionStorageKey(eventId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<RememberedPrediction> | null;
    if (!parsed || typeof parsed.id !== 'string') return null;
    if (typeof parsed.home !== 'number' || typeof parsed.away !== 'number') return null;
    return {
      id: parsed.id,
      home: parsed.home,
      away: parsed.away,
      prize: typeof parsed.prize === 'string' ? parsed.prize : null,
      at: typeof parsed.at === 'string' ? parsed.at : '',
    };
  } catch {
    return null;
  }
}

export function updatePredictionPrize(eventId: string, prize: string | null): void {
  const current = readPrediction(eventId);
  if (!current) return;
  rememberPrediction(eventId, { ...current, prize });
}

export function predictionOutcome(pred: RememberedPrediction, live: LiveMatchState): PredictionOutcome {
  const status = live.status ?? 'not_started';
  if (PENDING_STATUSES.has(status)) return 'pending';
  const hasScore = typeof live.home === 'number' && typeof live.away === 'number';
  if (!hasScore) return 'pending';
  if (FINAL_STATUSES.has(status)) {
    return live.home === pred.home && live.away === pred.away ? 'correct' : 'wrong';
  }
  return 'live';
}

export function predictionStripKey(outcome: PredictionOutcome): string {
  switch (outcome) {
    case 'live':
      return 'predictor.stripLive';
    case 'correct':
      return 'predictor.stripCorrect';
    case 'wrong':
      return 'predictor.stripWrong';
    default:
      return 'predictor.stripPending';
  }
}

/** Placeholders for the strip texts: h/a = the fan's prediction, lh/la = the match score ('–' when unknown). */
export function predictionStripParams(pred: RememberedPrediction, live: LiveMatchState): Record<string, string> {
  const show = (n: number | null | undefined) => (typeof n === 'number' ? String(n) : '–');
  return { h: String(pred.home), a: String(pred.away), lh: show(live.home), la: show(live.away) };
}
